// src/pages/Settings/SecurityLogin.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Switch } from '@headlessui/react';
import Permissions from './Permissions';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';


// Kept only Google and Password
const UI_TO_API_METHODS: Record<string, string> = {
  'Google Login': 'GOOGLE',
  'Login': 'PASSWORD',
};

const generateIdempotencyKey = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

const SecurityLogin: React.FC = () => {
  const [selectedTab] = useState<'Login' | 'Permissions'>('Login');

  // UI States
  const [allowedLoginsEnabled, setAllowedLoginsEnabled] = useState(true);
  const [allowedMethods, setAllowedMethods] = useState<string[]>([]);
  const [require2FA, setRequire2FA] = useState(false);
  const [limitUsers, setLimitUsers] = useState(true);
  const [allowAccess, setAllowAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- 1. FETCH DATA (GET) ---
  const fetchSecurityPolicy = useCallback(async () => {
    setLoading(true);
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      const response = await fetch(`${BASE_URL}/api/settings/security/policy`, {
        method: 'GET',
        headers: {
          'X-Idempotency-Key': generateIdempotencyKey(),
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch policy: ${response.status}`);
      const json = await response.json();
      const data = json.data;

      setAllowedLoginsEnabled(data.enforceAllowedLoginMethods);

      // Reverse mapping - kept only GOOGLE and PASSWORD
      const API_TO_UI_METHODS: Record<string, string> = {
        'GOOGLE': 'Google Login',
        'PASSWORD': 'Login',
      };

      const mappedMethods = data.allowedLoginMethods
        .map((code: string) => API_TO_UI_METHODS[code])
        .filter(Boolean);
      setAllowedMethods(mappedMethods);

      setRequire2FA(data.require2fa);
      setLimitUsers(data.exclusiveAccountMembership);
      setAllowAccess(data.supportAccessEnabled);

    } catch (err: any) {
      toast.error(err.message || 'Failed to load security settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSecurityPolicy();
  }, [fetchSecurityPolicy]);

  // --- 2. SAVE DATA (PUT) ---
  const saveSecurityPolicy = useCallback(async () => {
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      // Read the current state values directly inside the function
      const currentAllowedLoginsEnabled = await new Promise<boolean>((resolve) => {
        setAllowedLoginsEnabled((prev) => {
          resolve(prev);
          return prev;
        });
      });

      const currentAllowedMethods = await new Promise<string[]>((resolve) => {
        setAllowedMethods((prev) => {
          resolve(prev);
          return prev;
        });
      });

      const currentRequire2FA = await new Promise<boolean>((resolve) => {
        setRequire2FA((prev) => {
          resolve(prev);
          return prev;
        });
      });

      const currentLimitUsers = await new Promise<boolean>((resolve) => {
        setLimitUsers((prev) => {
          resolve(prev);
          return prev;
        });
      });

      const currentAllowAccess = await new Promise<boolean>((resolve) => {
        setAllowAccess((prev) => {
          resolve(prev);
          return prev;
        });
      });

      const apiMethods = currentAllowedMethods
        .map((uiLabel) => UI_TO_API_METHODS[uiLabel])
        .filter(Boolean);

      const payload = {
        enforceAllowedLoginMethods: currentAllowedLoginsEnabled,
        allowedLoginMethods: apiMethods,
        require2fa: currentRequire2FA,
        exclusiveAccountMembership: currentLimitUsers,
        supportAccessEnabled: currentAllowAccess,
      };

      const response = await fetch(`${BASE_URL}/api/settings/security/policy`, {
        method: 'PUT',
        headers: {
          'X-Idempotency-Key': generateIdempotencyKey(),
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `Failed to update: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) { /* ignore */ }
        throw new Error(errorMessage);
      }

      toast.success('Security settings updated successfully!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update security settings');
    }
  }, []);

  // Debounced Save Handler
  const triggerSave = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveSecurityPolicy();
    }, 1500);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const toggleMethod = (method: string) => {
    setAllowedMethods((prev) =>
      prev.includes(method)
        ? prev.filter((m) => m !== method)
        : [...prev, method]
    );
    triggerSave();
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]'></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className='text-[24px] font-bold text-[#3F5265] mb-2'>Security</h2>
      <p className='text-[#657584] text-[16px] mb-6'>
        Protect your data with advanced security features and encryption.
      </p>

      <div className='border rounded p-4 bg-white shadow-sm'>
        {selectedTab === 'Login' ? (
          <>
            <p className='text-[#657584] text-[16px] mb-6'>
              Control how users log in to your account.
            </p>

            {/* Configure login methods */}
            <div className='bg-[#F5F8FA] rounded-md shadow-sm mb-6'>
              <div className='flex items-center justify-between px-4 py-4'>
                <div>
                  <h3 className='font-semibold text-[18px] text-[#3F5265]'>
                    Configure allowed login methods
                  </h3>
                  <p className='text-[#657584] text-[16px] mt-1'>
                    Enforce which login methods are allowed for your users.
                  </p>
                </div>

                <Switch
                  checked={allowedLoginsEnabled}
                  onChange={(val) => { setAllowedLoginsEnabled(val); triggerSave(); }}
                  className={`${allowedLoginsEnabled ? 'bg-[#00899E]' : 'bg-gray-300'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition`}
                >
                  <span
                    className={`${allowedLoginsEnabled ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                  />
                </Switch>
              </div>

              {/* 🟢 Removed Apple, Microsoft, and Passkeys */}
              <div className='px-6 py-4 space-y-3 text-[14px] text-[#3F5265]'>
                {[
                  'Google Login',
                  'Login', // Maps to API's "PASSWORD"
                ].map((method) => (
                  <label
                    key={method}
                    className='flex items-center gap-2 cursor-pointer'
                  >
                    <input
                      type='checkbox'
                      checked={allowedMethods.includes(method)}
                      onChange={() => toggleMethod(method)}
                      disabled={!allowedLoginsEnabled}
                    />
                    {method}
                  </label>
                ))}
              </div>
            </div>

            {/* 2FA */}
            <div className='bg-[#F5F8FA] rounded-md shadow-sm mb-6 flex items-center justify-between px-4 py-4'>
              <div>
                <h3 className='font-semibold text-[18px] text-[#3F5265]'>
                  Require two-factor authentication (2FA)
                </h3>
                <p className='text-[16px] text-[#657584] mt-1'>
                  When this is selected all users are required to use 2FA to log in.
                </p>
              </div>
              <Switch
                checked={require2FA}
                onChange={(val) => { setRequire2FA(val); triggerSave(); }}
                className={`${require2FA ? 'bg-[#00899E]' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition`}
              >
                <span
                  className={`${require2FA ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                />
              </Switch>
            </div>

            {/* Limit users */}
            <div className='bg-[#F5F8FA] rounded-md shadow-sm mb-6 flex items-center justify-between px-4 py-4'>
              <div>
                <h3 className='font-semibold text-[18px] text-[#3F5265]'>
                  Limit users to only this account
                </h3>
                <p className='text-[16px] text-[#657584] mt-1'>
                  Users on this account will not be able to be added to
                  additional accounts in the future.
                </p>
              </div>
              <Switch
                checked={limitUsers}
                onChange={(val) => { setLimitUsers(val); triggerSave(); }}
                className={`${limitUsers ? 'bg-[#00899E]' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition`}
              >
                <span
                  className={`${limitUsers ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                />
              </Switch>
            </div>

            {/* Allow access */}
            <div className='bg-[#F5F8FA] rounded-md shadow-sm flex items-center justify-between px-4 py-4'>
              <div>
                <h3 className='font-semibold text-[18px] text-[#3F5265]'>
                  Allow access
                </h3>
                <p className='text-[16px] text-[#657584] mt-1'>
                  Enable this setting to let Autopath team members access your
                  account for technical support and guidance. This access is
                  limited and does not include all account data.
                </p>
              </div>
              <Switch
                checked={allowAccess}
                onChange={(val) => { setAllowAccess(val); triggerSave(); }}
                className={`${allowAccess ? 'bg-[#00899E]' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition`}
              >
                <span
                  className={`${allowAccess ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                />
              </Switch>
            </div>
          </>
        ) : selectedTab === 'Permissions' ? (
          <Permissions />
        ) : (
          <div className='text-center text-gray-500 text-[16px] py-20'>
            Work Pending
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityLogin;